import React, { useCallback, useState } from 'react';
import {
  Box,
  Alert,
  CircularProgress,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import Status from '../../Shared/Components/Status/Status';
import SaveButtons from '../../Shared/Components/SaveButtons/SaveButtons';

import { createCorrectionInvoiceSchema } from '../Validation/createCorrectionInvoice.schema';
import { useCorrectionPrefill } from '../hooks/useCorrectionPrefill';
import { useCreateCorrectionInvoiceMutation } from '../Service/correctionApi';
import type { CreateInvoiceForm } from '../Types/createInvoiceFormTypes';
import {
  buildInvoicePayload,
  DEFAULT_INVOICE_TEMPLATE_VARIANT,
} from '../utils/invoiceHelpers';
import { createInitialInvoiceForm } from '../utils/createInvoiceInitialState';
import { InvoiceType } from '../Types/invoicesTypes';
import { getFieldLabel } from '../utils/fieldLabels';
import { invoiceLogger } from '../utils/invoiceLogger';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { applyApiValidationErrorsToForm } from '../utils/invoiceApiErrorHandling';
import { buildValidationSummary } from '../utils/validationSummary';

import {
  InvoiceBasicsSection,
  InvoiceDocumentSection,
  InvoiceContractorSection,
  InvoiceItemsSection,
  InvoiceSellerSection,
  InvoiceCorrectionReasonSection,
} from '../Components';

/**
 * CreateCorrectionInvoice Component
 *
 * Purpose:
 * - Create correction invoice (faktury korygujące)
 * - Auto-fill form from base invoice
 * - User can modify any field before submitting
 * - Validates against business rules via backend API
 *
 * Route: /invoices/correct/:baseInvoiceId
 *
 * Business Rules (validated by backend):
 * 1. Base invoice must exist
 * 2. Base invoice cannot be Draft or Cancelled
 * 3. Cannot correct a correction invoice
 * 4. Scheme must match base invoice
 * 5. Currency must match base invoice
 *
 * Flow:
 * 1. Parse baseInvoiceId from URL params
 * 2. Fetch base invoice via useGetInvoiceForCorrectionQuery
 * 3. Auto-fill form via useCorrectionPrefill hook
 * 4. User modifies fields (items, quantities, prices, etc.)
 * 5. Submit correction invoice via POST /api/invoices
 * 6. Backend generates correction number: {BaseNumber}-K{Sequence}
 * 7. Navigate to invoice detail page
 */
const CreateCorrectionInvoice: React.FC = () => {
  const navigate = useNavigate();
  const { baseInvoiceId } = useParams<{ baseInvoiceId: string }>();
  const parsedBaseInvoiceId = baseInvoiceId ? parseInt(baseInvoiceId, 10) : undefined;

  // Template variant state (Modern PDF only)
  const [templateVariant, setTemplateVariant] = useState<string>(DEFAULT_INVOICE_TEMPLATE_VARIANT);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);
  const [submitOutcome, setSubmitOutcome] = useState<'idle' | 'success' | 'error'>('idle');

  invoiceLogger.debug('[CreateCorrectionInvoice] 🎯 Component mounted with:', {
    baseInvoiceId_raw: baseInvoiceId,
    parsedBaseInvoiceId,
    isValidId: parsedBaseInvoiceId !== undefined && parsedBaseInvoiceId > 0,
    templateVariant,
  });

  // Debug: Log template variant changes
  React.useEffect(() => {
    invoiceLogger.debug(
      '[CreateCorrectionInvoice] 🎨 Template variant changed to:',
      templateVariant
    );
  }, [templateVariant]);

  // React Hook Form setup
  const defaultVals = {
    ...createInitialInvoiceForm(),
    correctionInvoiceId: parsedBaseInvoiceId,
    type: InvoiceType.Correction, // Set correction type by default
  };

  const {
    control,
    handleSubmit: rhfHandleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    trigger, // ✅ CRITICAL: Manual validation trigger
    formState: { errors, isValid, isSubmitting },
  } = useForm<CreateInvoiceForm>({
    defaultValues: defaultVals,
    resolver: yupResolver(createCorrectionInvoiceSchema) as any,
    mode: 'onSubmit', // ✅ CRITICAL: Validate on submit (not onChange) to prevent premature errors
    reValidateMode: 'onChange', // ✅ Re-validate on change after first submit
  });
  const validationSummary = React.useMemo(() => buildValidationSummary(errors), [errors]);

  // Debug: Log form errors
  React.useEffect(() => {
    const errorKeys = Object.keys(errors);
    invoiceLogger.debug('[CreateCorrectionInvoice] 🔍 Checking errors:', {
      errorsObjectKeysLength: errorKeys.length,
      errorsKeys: errorKeys,
      errorsRaw: errors,
    });

    if (errorKeys.length > 0) {
      invoiceLogger.error('[CreateCorrectionInvoice] ❌ Form validation ERRORS:', errors);
      invoiceLogger.error('[CreateCorrectionInvoice] ❌ Error fields:', errorKeys);

      // Create detailed error report
      const errorReport = errorKeys.map((field) => ({
        field,
        message: (errors as any)[field]?.message || 'No message',
        type: (errors as any)[field]?.type || 'unknown',
      }));

      invoiceLogger.error('[CreateCorrectionInvoice] Error report:', errorReport);

      // Log each error in detail
      errorKeys.forEach((field) => {
        invoiceLogger.error(`[CreateCorrectionInvoice] ❌ Field "${field}":`, {
          message: (errors as any)[field]?.message,
          type: (errors as any)[field]?.type,
          ref: (errors as any)[field]?.ref,
          fullError: (errors as any)[field],
        });
      });
    }
  }, [errors]);

  React.useEffect(() => {
    invoiceLogger.debug('[CreateCorrectionInvoice] 📋 Form state:', {
      isValid,
      isSubmitting,
      hasErrors: Object.keys(errors).length > 0,
      errorCount: Object.keys(errors).length,
    });
  }, [isValid, isSubmitting, errors]);

  // Watch form fields
  const watchScheme = watch('scheme');
  const watchReservationId = watch('reservationId');
  const watchType = watch('type');

  // Auto-fill form from base invoice
  const {
    baseInvoice,
    isLoading: isPrefillLoading,
    error: prefillError,
  } = useCorrectionPrefill(parsedBaseInvoiceId, setValue);

  // Create correction mutation
  const [createCorrection, createStatus] = useCreateCorrectionInvoiceMutation();

  // ===================================================================
  // SUBMIT HANDLER
  // ===================================================================
  const onSubmit = useCallback(
    async (rhfFormData: CreateInvoiceForm) => {
      clearErrors();
      setSubmitErrorMessage(null);
      setSubmitOutcome('idle');

      invoiceLogger.debug('[CreateCorrectionInvoice] 📤 onSubmit called with:', {
        parsedBaseInvoiceId,
        templateVariant,
        rhfFormData_type: rhfFormData.type,
        rhfFormData_scheme: rhfFormData.scheme,
      });

      if (!parsedBaseInvoiceId) {
        invoiceLogger.error('[CreateCorrectionInvoice] ❌ Missing parsedBaseInvoiceId');
        setSubmitOutcome('error');
        setSubmitErrorMessage('Brak ID faktury bazowej. Odśwież widok i spróbuj ponownie.');
        throw new Error('Missing base invoice id');
      }

      // Ensure correctionInvoiceId is set (type comes from form)
      const payload = buildInvoicePayload({
        ...rhfFormData,
        correctionInvoiceId: parsedBaseInvoiceId,
        templateVariant, // Pass template variant to backend
      });

      invoiceLogger.debug('[CreateCorrectionInvoice] 📦 Built payload:', {
        templateVariant: payload.templateVariant,
        correctionInvoiceId: payload.correctionInvoiceId,
        type: payload.type,
        itemsCount: payload.items?.length,
      });

      try {
        invoiceLogger.debug('[CreateCorrectionInvoice] 🚀 Calling createCorrection API...');
        invoiceLogger.debug(
          '[CreateCorrectionInvoice] 📦 Full payload:',
          JSON.stringify(payload, null, 2)
        );
        const result = await createCorrection(payload as any).unwrap();
        invoiceLogger.debug(
          '[CreateCorrectionInvoice] ✅ Backend response SUCCESS:',
          JSON.stringify(result, null, 2)
        );
        setSubmitOutcome('success');
        navigate(`/invoices/edit/${result.id}`);
      } catch (err: any) {
        applyApiValidationErrorsToForm(err, setError);

        const status = typeof err?.status === 'number' ? err.status : undefined;
        const fallbackMessage =
          status && status >= 500
            ? 'Błąd serwera podczas zapisu korekty. Spróbuj ponownie za chwilę.'
            : 'Nie udało się utworzyć korekty faktury. Zweryfikuj dane i spróbuj ponownie.';

        setSubmitOutcome('error');
        setSubmitErrorMessage(extractApiErrorMessage(err, fallbackMessage));

        invoiceLogger.error('[CreateCorrectionInvoice] ❌ Backend ERROR:', err);
        invoiceLogger.error('[CreateCorrectionInvoice] ❌ Error status:', err?.status);
        invoiceLogger.error(
          '[CreateCorrectionInvoice] ❌ Error data:',
          JSON.stringify(err?.data, null, 2)
        );
        invoiceLogger.error(
          '[CreateCorrectionInvoice] ❌ Error message:',
          err?.data?.message || err?.message
        );
        throw err;
      }
    },
    [clearErrors, parsedBaseInvoiceId, createCorrection, navigate, setError, templateVariant]
  );

  // ===================================================================
  // LOADING STATE
  // ===================================================================
  if (!parsedBaseInvoiceId || isNaN(parsedBaseInvoiceId)) {
    return (
      <CustomCard title="Błąd" icon={<ReceiptLongIcon />}>
        <Alert severity="error">Nieprawidłowy ID faktury bazowej w URL</Alert>
      </CustomCard>
    );
  }

  if (isPrefillLoading) {
    return (
      <CustomCard title="Ładowanie faktury..." icon={<ReceiptLongIcon />}>
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </CustomCard>
    );
  }

  // ===================================================================
  // ERROR STATE (Backend validation errors)
  // ===================================================================
  if (prefillError) {
    const errorMessage = extractApiErrorMessage(
      prefillError,
      'Nie można załadować faktury bazowej'
    );

    return (
      <CustomCard title="Błąd walidacji" icon={<ReceiptLongIcon />}>
        <Alert severity="error">
          <strong>Nie można utworzyć korekty:</strong>
          <br />
          {errorMessage}
        </Alert>
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            Możliwe przyczyny:
          </Typography>
          <ul>
            <li>Faktura ma status Draft (zmień na Issued)</li>
            <li>Faktura została anulowana (Cancelled)</li>
            <li>Próbujesz korygować fakturę korygującą (wybierz fakturę bazową)</li>
          </ul>
        </Box>
        <Box mt={3}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/invoices')}
          >
            Powrót do listy faktur
          </Button>
        </Box>
      </CustomCard>
    );
  }

  if (!baseInvoice) {
    return (
      <CustomCard title="Faktura nie znaleziona" icon={<ReceiptLongIcon />}>
        <Alert severity="warning">Nie znaleziono faktury o ID: {parsedBaseInvoiceId}</Alert>
        <Box mt={3}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/invoices')}
          >
            Powrót do listy faktur
          </Button>
        </Box>
      </CustomCard>
    );
  }

  // ===================================================================
  // MAIN FORM
  // ===================================================================
  return (
    <CustomCard title={`Korekta faktury: ${baseInvoice.invoiceNumber}`} icon={<ReceiptLongIcon />}>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Faktura bazowa:</strong> {baseInvoice.invoiceNumber}
          <br />
          <strong>Data wystawienia:</strong> {baseInvoice.issueDate}
          <br />
          <strong>Kwota brutto:</strong> {baseInvoice.totalGross.toFixed(2)} {baseInvoice.currency}
          <br />
          <strong>Typ:</strong> {baseInvoice.type}
        </Typography>
      </Alert>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Uwaga:</strong> Formularz został automatycznie wypełniony danymi z faktury
          bazowej. Możesz modyfikować dowolne pola (pozycje, ceny, dane kontrahenta) przed
          zapisaniem korekty.
        </Typography>
      </Alert>

      {/* Wait for prefill to complete before rendering form to avoid MUI warnings */}
      {isPrefillLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* FORM SECTIONS */}
          <form onSubmit={rhfHandleSubmit(onSubmit)}>
            <InvoiceBasicsSection
              control={control as any}
              errors={errors}
              setValue={setValue}
              nextNumber={{
                number: baseInvoice?.invoiceNumber || '',
                isPreview: true,
                note: `Korekta do: ${baseInvoice?.invoiceNumber || 'Ładowanie...'}`,
              }}
              isNextLoading={false}
              isCorrectionInvoice={true}
            />

            <InvoiceDocumentSection control={control as any} errors={errors} setValue={setValue} />

            {/* Powód korekty - WYMAGANE dla faktur korygujących */}
            <InvoiceCorrectionReasonSection control={control as any} errors={errors} />

            {/* Wybór szablonu PDF */}
            <Box mt={3}>
              <CustomCard title="Szablon PDF">
                <FormControl fullWidth>
                  <InputLabel>Szablon PDF</InputLabel>
                  <Select
                    value={templateVariant}
                    onChange={(e) => {
                      invoiceLogger.debug(
                        '[CreateCorrectionInvoice] 🎨 Template SELECT onChange:',
                        {
                          newValue: e.target.value,
                          oldValue: templateVariant,
                        }
                      );
                      setTemplateVariant(e.target.value);
                    }}
                    label="Szablon PDF"
                  >
                    <MenuItem value="modern">
                      Modern (Premium) - kolorowy z tabelami przed/po korekcie
                    </MenuItem>
                  </Select>
                </FormControl>
              </CustomCard>
            </Box>

            <InvoiceSellerSection control={control as any} errors={errors} />

            <InvoiceContractorSection control={control as any} errors={errors} />

            <InvoiceItemsSection
              control={control as any}
              setValue={setValue}
              errors={errors}
              reservationId={watchReservationId}
              scheme={watchScheme}
              invoiceType={watchType}
              isCorrectionInvoice={true}
            />

            {/* ✅ Error Summary - sticky alert at bottom when validation errors exist */}
            <Collapse in={validationSummary.length > 0}>
              <Alert
                severity="error"
                icon={<ErrorOutlineIcon />}
                sx={{
                  mt: 2,
                  mb: 2,
                  position: 'sticky',
                  bottom: 80, // Above SaveButtons
                  zIndex: 1000,
                  boxShadow: 3,
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Formularz zawiera błędy walidacji ({validationSummary.length})
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Proszę poprawić poniższe pola przed zapisaniem:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {validationSummary.map(({ field, message }) => (
                    <li key={`${field}-${message ?? 'validation'}`}>
                      <Typography variant="body2">
                        <strong>{getFieldLabel(field)}:</strong> {message || 'Błąd walidacji'}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            </Collapse>

            {/* Submit Section */}
            <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
              <Status
                isLoading={createStatus.isLoading}
                isError={false}
                isSuccess={submitOutcome === 'success'}
              />

              <SaveButtons
                isUpdating={createStatus.isLoading}
                handleSubmit={async (_e, shouldRedirect) => {
                  invoiceLogger.debug(
                    '═══════════════════════════════════════════════════════════════'
                  );
                  invoiceLogger.debug(
                    '[CreateCorrectionInvoice] �️  BUTTON CLICKED:',
                    shouldRedirect ? 'ZAPISZ I WYJDŹ' : 'ZAPISZ I KONTYNUUJ'
                  );
                  invoiceLogger.debug(
                    '═══════════════════════════════════════════════════════════════'
                  );

                  setSubmitErrorMessage(null);
                  setSubmitOutcome('idle');

                  // ✅ CRITICAL: Manually trigger validation to ensure errors are up-to-date
                  invoiceLogger.debug(
                    '[CreateCorrectionInvoice] 🔍 Manually triggering validation...'
                  );
                  const validationResult = await trigger();

                  invoiceLogger.debug('[CreateCorrectionInvoice] 📊 Validation result:', {
                    validationPassed: validationResult,
                    shouldRedirect,
                    templateVariant,
                    createStatus_isLoading: createStatus.isLoading,
                    formState_isValid: isValid,
                    formState_isSubmitting: isSubmitting,
                    formState_errorsCount: Object.keys(errors).length,
                    formState_errors_RAW: errors,
                    formState_errors_KEYS: Object.keys(errors),
                  });

                  if (!validationResult || Object.keys(errors).length > 0) {
                    invoiceLogger.error(
                      '═══════════════════════════════════════════════════════════════'
                    );
                    invoiceLogger.error(
                      '[CreateCorrectionInvoice] ⛔ BLOCKING SUBMIT - Form has validation errors:'
                    );
                    invoiceLogger.error(JSON.stringify(errors, null, 2));
                    invoiceLogger.error(
                      '═══════════════════════════════════════════════════════════════'
                    );
                    setSubmitOutcome('error');
                    setSubmitErrorMessage(
                      'Formularz zawiera błędy walidacji. Popraw oznaczone pola i spróbuj ponownie.'
                    );
                    return;
                  }

                  // ✅ CRITICAL: Track submission success
                  let submissionPassed = false;

                  try {
                    invoiceLogger.debug(
                      '[CreateCorrectionInvoice] 🚀 Calling rhfHandleSubmit(onSubmit)...'
                    );
                    await rhfHandleSubmit(onSubmit)();

                    invoiceLogger.debug('[CreateCorrectionInvoice] ✅ Submission successful!');
                    submissionPassed = true; // ✅ Mark submission as successful
                  } catch (err) {
                    invoiceLogger.error('[CreateCorrectionInvoice] ❌ Submit failed', err);
                    submissionPassed = false; // ❌ Submission failed
                  }

                  // ✅ CRITICAL: Only redirect if submission passed
                  if (submissionPassed && shouldRedirect) {
                    invoiceLogger.debug(
                      '[CreateCorrectionInvoice] 🔙 Navigating back in history (shouldRedirect=true, submission OK)'
                    );
                    navigate(-1);
                  } else if (submissionPassed && !shouldRedirect) {
                    invoiceLogger.debug(
                      '[CreateCorrectionInvoice] ✏️  Staying on page (shouldRedirect=false, submission OK)'
                    );
                  } else {
                    invoiceLogger.warn(
                      '[CreateCorrectionInvoice] ⚠️  NOT redirecting - submission failed'
                    );
                  }
                }}
                handleCancel={() => navigate('/invoices')}
              />
            </Box>

            {submitErrorMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <strong>Błąd podczas zapisywania:</strong>
                <br />
                {submitErrorMessage}
              </Alert>
            )}
          </form>
        </>
      )}
    </CustomCard>
  );
};

export default CreateCorrectionInvoice;
